
import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { tables } from '@/lib/supabase';
import { Location } from '@/types/locationTypes';
import { useAuth } from '@/hooks/useAuth';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { MapPin } from 'lucide-react';

interface LocationFilterProps {
  selectedLocationId: string | null;
  onLocationChange: (locationId: string | null) => void;
}

export const LocationFilter: React.FC<LocationFilterProps> = ({ 
  selectedLocationId, 
  onLocationChange 
}) => {
  const { organizationId } = useAuth();

  const { data: locations = [], isLoading } = useQuery({
    queryKey: ['locations'],
    queryFn: async () => {
      const { data, error } = await tables.locations()
        .select('*')
        .eq('organization_id', organizationId || '');
        
      if (error) throw error;
      return data as Location[];
    },
    enabled: !!organizationId,
  });

  const handleChange = (value: string) => {
    onLocationChange(value === 'all' ? null : value);
  };

  const getLocationTypeLabel = (type: string | undefined) => {
    switch(type) {
      case 'studio': return 'Studio';
      case 'external': return 'External';
      case 'virtual': return 'Virtual';
      default: return 'Other';
    }
  };

  return (
    <Select
      value={selectedLocationId || 'all'}
      onValueChange={handleChange}
      disabled={isLoading}
    >
      <SelectTrigger className="w-[180px]">
        <SelectValue placeholder="Filter by location" />
      </SelectTrigger>
      <SelectContent>
        <SelectItem value="all">
          <div className="flex items-center">
            <MapPin className="h-4 w-4 mr-2 text-muted-foreground" />
            All Locations
          </div>
        </SelectItem>
        <SelectItem value="virtual">
          <div className="flex items-center">
            <MapPin className="h-4 w-4 mr-2 text-blue-500" />
            Virtual Classes
          </div>
        </SelectItem>
        <SelectItem value="offsite">
          <div className="flex items-center">
            <MapPin className="h-4 w-4 mr-2 text-amber-500" />
            Off-site Events
          </div>
        </SelectItem>
        {locations.map((location) => (
          <SelectItem key={location.id} value={location.id}>
            <div className="flex items-center">
              <MapPin 
                className={`h-4 w-4 mr-2 ${
                  location.type === 'virtual' ? 'text-blue-500' :
                  location.type === 'external' ? 'text-amber-500' :
                  'text-emerald-500'
                }`} 
              />
              {location.name} ({getLocationTypeLabel(location.type)})
            </div>
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
};

export default LocationFilter;
